"use client";
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import SchoolPickerModal from "./SchoolPicker";
import SubmissionSuccessCard from "./SubmissionSuccessCard";

// ---------------------------------------------------------------------------
// Locked field — shows selected school data, not editable
// ---------------------------------------------------------------------------
function LockedField({ value, placeholder, className = "" }) {
  return (
    <div
      className={`p-4 rounded-2xl border-2 border-sky-200 bg-sky-50 text-sm text-slate-600 relative ${className}`}
      title="Auto-filled from selected school"
    >
      {value ? (
        <span>{value}</span>
      ) : (
        <span className="text-slate-300">{placeholder}</span>
      )}
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sky-300 text-xs">🔒</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Form Component
// ---------------------------------------------------------------------------
export default function StudentForm() {
  const initialFormState = {
    titleName: "",
    firstName: "",
    lastName: "",
    grade: "",
    webLink: "",
    email: "",
    schoolName: "",
    ncessch: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    lat: "",
    lon: "",
    enrollment: "",
    freeLunchPct: "",
    verificationCode: "",
    country: "",
    schoolType: "", 
    fullAddress: "",
    schoolWebsite: "",
    poverty: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [showPicker, setShowPicker] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [isCustomSchool, setIsCustomSchool] = useState(false);
  const [isVerified, setIsVerified] = useState(true);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSchoolSelect = (school) => {
    setSelectedSchool(school);
    setIsCustomSchool(false);
    setFormData((prev) => ({
      ...prev,
      schoolName: school.name,
      ncessch: String(school.ncessch),
      street: school.street,
      city: school.city,
      state: school.state,
      zip: school.zip,
      lat: String(school.lat || ""),
      lon: String(school.lon || ""),
      enrollment: String(school.enrollment || ""),
      freeLunchPct: String(school.freeLunchPct || ""),
      country: "United States",
      schoolType: "Public",
    }));
    setShowPicker(false);
  };

  const handleCustomSchoolSubmit = (e) => {
    e.preventDefault();
    setIsCustomSchool(true);
    setSelectedSchool({ 
      name: formData.schoolName, 
      city: formData.fullAddress.split(',')[1]?.trim() || "Custom", 
      state: formData.fullAddress.split(',')[2]?.trim() || formData.country || "School" 
    });
    setShowCustomModal(false);
  };

  const clearSchool = () => {
    setSelectedSchool(null);
    setIsCustomSchool(false);
    setFormData((prev) => ({
      ...prev,
      schoolName: "",
      ncessch: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      lat: "",
      lon: "",
      enrollment: "",
      freeLunchPct: "",
      schoolType: "",
      fullAddress: "",
      schoolWebsite: "",
      poverty: "",
      country: "",
    }));
  };

  const handleResetForm = () => {
    setFormData(initialFormState);
    setSelectedSchool(null);
    setIsCustomSchool(false);
    setSubmissionResult(null);
    setIsSubmitted(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSchool) {
      alert("Please select or enter your school details first! 🏫");
      return;
    }

    let cleanedData = { ...formData };
    if (cleanedData.webLink && !cleanedData.webLink.startsWith("http")) {
      cleanedData.webLink = `https://${cleanedData.webLink}`;
    }

    try {
      const response = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cleanedData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Server Error:", errorText);
        alert("Failed to submit form. Please check requirements.");
        return;
      }

      const result = await response.json();

      if (result.success) {
        setSubmissionResult(result);
        setIsSubmitted(true);
      } else {
        alert(result.error || "An error occurred during submission.");
      }
    } catch (error) {
      console.error("Submission error:", error);
    }
  };

  const customModalMarkup = (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[99999]">
      <div className="bg-white p-6 rounded-[2rem] max-w-sm w-[400px] shadow-2xl border-t-8 border-amber-400 max-h-[90vh] overflow-y-auto relative styleScrollbar">
        <button 
          type="button"
          onClick={() => setShowCustomModal(false)}
          className="absolute top-5 left-4 text-slate-400 hover:text-slate-600 font-bold text-xl z-10"
        >
          ✕
        </button>
        
        <h3 className="text-2xl font-black text-slate-800 mb-6 flex items-center gap-2 pl-7">
           School Details
        </h3>
        
        <form onSubmit={handleCustomSchoolSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">School Name</label>
            <input
              type="text" required name="schoolName" placeholder="e.g. St. Mary Academy"
              value={formData.schoolName} onChange={handleChange}
              className="w-full p-3.5 bg-slate-50 border-2 border-slate-100 rounded-xl outline-none focus:border-amber-400 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">School Type</label>
            <input
              type="text" required name="schoolType" placeholder="Public, Private, Charter"
              value={formData.schoolType} onChange={handleChange}
              className="w-full p-3.5 bg-slate-50 border-2 border-slate-100 rounded-xl outline-none focus:border-amber-400 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Full Address</label>
            <input
              type="text" required name="fullAddress" placeholder="Street, City, State, Postal Code"
              value={formData.fullAddress} onChange={handleChange}
              className="w-full p-3.5 bg-slate-50 border-2 border-slate-100 rounded-xl outline-none focus:border-amber-400 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Country</label>
              <input
                type="text" required name="country" placeholder="e.g. Canada"
                value={formData.country} onChange={handleChange}
                className="w-full p-3.5 bg-slate-50 border-2 border-slate-100 rounded-xl outline-none focus:border-amber-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">School Website Link</label>
              <input
                type="text" name="schoolWebsite" placeholder="Optional"
                value={formData.schoolWebsite} onChange={handleChange}
                className="w-full p-3.5 bg-slate-50 border-2 border-slate-100 rounded-xl outline-none focus:border-amber-400 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Student Enrollment</label>
              <input
                type="text" name="enrollment" placeholder="e.g. 450"
                value={formData.enrollment} onChange={handleChange}
                className="w-full p-3.5 bg-slate-50 border-2 border-slate-100 rounded-xl outline-none focus:border-amber-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Poverty %</label>
              <input
                type="text" name="poverty" placeholder="e.g. 45"
                value={formData.poverty} onChange={handleChange}
                className="w-full p-3.5 bg-slate-50 border-2 border-slate-100 rounded-xl outline-none focus:border-amber-400 text-sm"
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 rounded-xl transition-all text-sm mt-4 shadow-md uppercase tracking-wider"
          >
            Confirm School Details ✓
          </button>
        </form>
      </div>
    </div>
  );

  if (isSubmitted) {
    return (
      <div className="w-full flex justify-center py-20 px-4 bg-orange-50/50">
        <SubmissionSuccessCard
          responseData={submissionResult}
          onResetForm={handleResetForm}
        />
      </div>
    );
  }

  return (
    <>
      {showPicker && (
        <SchoolPickerModal
          onSelect={handleSchoolSelect}
          onClose={() => setShowPicker(false)}
        />
      )}

      {showCustomModal && mounted && createPortal(customModalMarkup, document.body)}

      <div className="w-full flex justify-center py-20 px-4 bg-orange-50/50">
        <form onSubmit={handleSubmit} className="w-[480px]">
          <div className="bg-white p-10 pb-12 rounded-[2.5rem] shadow-2xl border-t-8 border-sky-400 relative rotate-1 hover:rotate-0 transition-transform duration-500 ease-in-out">
            <div className="absolute -top-6 -right-6 text-5xl bg-white rounded-full p-2 shadow-lg border border-slate-100">
              📎
            </div>

            <h2 className="text-3xl font-bold text-sky-500 mb-10 flex items-center gap-2">
              Submit a list
            </h2>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <input
                  className="p-4 rounded-2xl border-2 border-slate-100 focus:border-amber-400 outline-none transition-all placeholder:text-slate-300 text-sm bg-slate-50"
                  name="titleName" placeholder="Ms. or Mr."
                  value={formData.titleName} onChange={handleChange} required
                />
                <input
                  className="p-4 rounded-2xl border-2 border-slate-100 focus:border-amber-400 outline-none transition-all placeholder:text-slate-300 text-sm bg-slate-50"
                  name="firstName" placeholder="First Name"
                  value={formData.firstName} onChange={handleChange} required
                />
              </div>
              <div className="grid grid-cols-1 gap-4">
                <input
                  className="p-4 rounded-2xl border-2 border-slate-100 focus:border-amber-400 outline-none transition-all placeholder:text-slate-300 text-sm bg-slate-50"
                  name="lastName" placeholder="Last Name"
                  value={formData.lastName} onChange={handleChange} required
                />
              </div>

              <div>
                {!selectedSchool ? (
                  <div className="space-y-2 text-center">
                    <button
                      type="button"
                      onClick={() => setShowPicker(true)}
                      className="w-full p-4 rounded-2xl border-2 border-dashed border-sky-300 hover:border-sky-400 hover:bg-sky-50 text-sky-400 hover:text-sky-500 font-semibold text-sm transition-all flex items-center justify-center gap-2"
                    >
                      🏫 Select Your School
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCustomModal(true)}
                      className="text-xs font-semibold text-slate-400 hover:text-sky-500 hover:underline transition-colors cursor-pointer inline-block pt-1"
                    >
                      Not a US public school?
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between bg-sky-50 border-2 border-sky-200 rounded-2xl px-4 py-3">
                      <div>
                        <div className="text-xs font-semibold text-sky-500 uppercase tracking-wide mb-0.5">
                          {isCustomSchool ? "Custom School Added ✓" : "School Selected ✓"}
                        </div>
                        <div className="text-sm font-bold text-slate-700">{selectedSchool.name}</div>
                        <div className="text-xs text-slate-400">
                          {selectedSchool.city}, {selectedSchool.state}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={clearSchool}
                        className="text-xs text-slate-400 hover:text-red-400 transition-colors underline underline-offset-2 ml-4 shrink-0"
                      >
                        Change
                      </button>
                    </div>

                    {isCustomSchool ? (
                      <div className="bg-amber-50/40 border border-amber-200/60 rounded-xl p-3 text-xs text-amber-800 space-y-1">
                        <div><strong>Type:</strong> <span className="capitalize">{formData.schoolType}</span></div>
                        <div><strong>Address:</strong> {formData.fullAddress}</div>
                        <div><strong>Country:</strong> {formData.country}</div>
                        {formData.schoolWebsite && <div><strong>Website:</strong> {formData.schoolWebsite}</div>}
                      </div>
                    ) : (
                      <>
                        <LockedField value={formData.schoolName} placeholder="School Name" />
                        <LockedField value={formData.street} placeholder="Street Address" />
                        <div className="grid grid-cols-5 gap-3">
                          <LockedField value={formData.city} placeholder="City" className="col-span-2" />
                          <LockedField value={formData.state} placeholder="St" className="col-span-1" />
                          <LockedField value={formData.zip} placeholder="Zip Code" className="col-span-2" />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <input
                  className="w-full p-4 rounded-2xl border-2 border-slate-100 focus:border-amber-400 outline-none transition-all placeholder:text-slate-300 text-sm bg-slate-50"
                  name="email" type="email" placeholder="School Email ✉️"
                  value={formData.email} onChange={handleChange} required
                />
              </div>

              <div className="grid grid-cols-5 gap-4">
              <select
                className={`col-span-2 p-4 rounded-2xl border-2 border-slate-100 focus:border-amber-400 outline-none transition-all text-sm bg-slate-50 ${
                  formData.grade ? "text-slate-600" : "text-slate-300"
                }`}
                name="grade"
                value={formData.grade}
                onChange={handleChange}
                required
              >
                <option value="" disabled hidden>Grade</option>
                <option value="PK" style={{ color: "#475569" }}>Pre-K</option>
                <option value="K" style={{ color: "#475569" }}>K</option>
                <option value="1" style={{ color: "#475569" }}>1st</option>
                <option value="2" style={{ color: "#475569" }}>2nd</option>
                <option value="3" style={{ color: "#475569" }}>3rd</option>
                <option value="4" style={{ color: "#475569" }}>4th</option>
                <option value="5" style={{ color: "#475569" }}>5th</option>
                <option value="6-8" style={{ color: "#475569" }}>6-8</option>
                <option value="9-12" style={{ color: "#475569" }}>9-12</option>
                <option value="SPED" style={{ color: "#475569" }}>SPED</option>
                <option value="K-5" style={{ color: "#475569" }}>K-5</option>
                <option value="K-8" style={{ color: "#475569" }}>K-8</option>
                <option value="K-12" style={{ color: "#475569" }}>K-12</option>
              </select>
                <input
                  className="col-span-3 p-4 rounded-2xl border-2 border-slate-100 focus:border-amber-400 outline-none transition-all placeholder:text-slate-300 text-sm bg-slate-50"
                  name="webLink" type="text" placeholder="Amazon Wishlist 🔗"
                  value={formData.webLink} onChange={handleChange} required
                />
              </div>

              <div className="text-center pt-4">
                <button
                  type="submit"
                  disabled={!isVerified}
                  className={`w-full font-bold py-5 rounded-2xl transition-all text-xl 
                    ${isVerified
                      ? "bg-amber-400 hover:bg-amber-500 text-amber-950 shadow-[0_5px_0_0_#ca8a04] active:shadow-none active:translate-y-1"
                      : "bg-slate-200 text-slate-400 cursor-not-allowed"
                    }`}
                >
                  {isVerified ? "Submit My List" : "Verify Email to Continue 🔒"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}